import { FilePdfFilled, FileTextFilled, FileWordFilled } from "@ant-design/icons";
import { message, Spin, Upload } from "antd";
import ImgCrop from "antd-img-crop";
import { RcFile, UploadFile } from "antd/lib/upload";
import React from "react";
import { useEffect, useState } from "react";
import FileUpload from "../../assets/FileUpload";
import { bytesToMB, imageResize, isImage, isImageMoreThanXMB } from "../../helpers/image";
import { FileProperties } from "../../types/file.type";

/**
 * Custom Upload component for From
 *
 * Use this inside
 * <Form.Item>
 *  <UplaodInput />
 * </Form.Item>
 *
 * File will pass as form instance value.
 */

interface UploadInputProps {
	value?: File | FileProperties;
	onChange?: (value?: File | Blob | RcFile | FileProperties | UploadFile<any>) => void;
	maxFileSize?: number;
	allowedExtension?: string[];
	additionalHint?: string;
	disabled?: boolean;

	enableAutoResizeForImage?: boolean;
	configAutoResizeForImage?: {
		maxFileWidth: number;
	};
	showInfo?: boolean;
	isCircle?: boolean;
	withImgCrop?: boolean;
	imgCropAspect?: number;
}
const defaultProps: UploadInputProps = {
	maxFileSize: 2, // in MB
	allowedExtension: ["jpg", "png"],
};
const { Dragger } = Upload;
const UploadInput: React.FC<UploadInputProps> = (props) => {
	const {
		value,
		onChange,
		maxFileSize,
		allowedExtension,
		additionalHint,
		disabled,
		showInfo = true,
	} = {
		...defaultProps,
		...props,
	};

	const [previewOriginalFileSize, setPreviewOriginalFileSize] = useState<string>();
	const [previewFileSize, setPreviewFileSize] = useState<string>();
	const [previewImage, setPreviewImage] = useState<string>();
	const [previewName, setPreviewName] = useState<string>();
	const [isResizingLoading, setIsResizingLoading] = useState(false);

	useEffect(() => {
		console.info("value change, will set preview");

		if (!value) {
			console.info("no value");
			setPreviewImage(undefined);
			return;
		}

		let name = "";
		if (value instanceof File) {
			console.info("value instanceof File");
			setPreviewFileSize(bytesToMB(value.size) + "MB");
			setPreviewOriginalFileSize(bytesToMB(value.size) + "MB");

			if (isImage(value)) {
				console.info("type image");
				const reader = new FileReader();
				reader.onloadend = () => {
					console.info("onloadend, will set");
					setPreviewImage(reader.result as string);
				};
				reader.readAsDataURL(value);
			} else {
				console.info("not image, no preview");
				setPreviewImage(undefined);
			}
			name = value.name;
		} else {
			console.info("NOT - value instanceof File");
			const valueFileUrl = value as FileProperties;
			if (isImage(valueFileUrl as any)) {
				console.info("type image", valueFileUrl.fileUrl);
				setPreviewImage(valueFileUrl.fileUrl);
			} else {
				console.info("not image");
				setPreviewImage(undefined);
			}
			name = valueFileUrl.originalName || "";
		}

		const maxLength = 40;
		const truncatedName = name.length > maxLength ? name.substring(0, maxLength) + "..." : name;

		const previewContent: string | undefined = `${truncatedName}${truncatedName !== name ? `.${name.split(".").pop()}` : ""
			}`;

		setPreviewName(previewContent);
	}, [value]);

	const _handleSelectImage = async (file: File) => {
		console.info("_handleSelectImage original", file);
		let newFile: File | Blob | RcFile | UploadFile<any> = file;
		setPreviewOriginalFileSize(bytesToMB(file.size) + "MB");
		if (isImage(newFile) && props.enableAutoResizeForImage) {
			setIsResizingLoading(true);
			newFile = await imageResize(file, props.configAutoResizeForImage?.maxFileWidth);
			console.info("newFile after resize", newFile);
			setIsResizingLoading(false);
		}
		const isLimitSize = isImageMoreThanXMB(newFile, maxFileSize ?? 2);
		if (isLimitSize) {
			message.error(`File must smaller than ${maxFileSize}MB!`);
			return Upload.LIST_IGNORE;
		}

		if (newFile) {
			onChange?.(newFile);
		}
	};

	const PreviewIcon: React.FC = () => {
		let ext = previewName?.split(".").pop();

		switch (ext) {
			case "pdf":
				return <FilePdfFilled style={{ fontSize: 60, marginBottom: 20, color: "#DF2A2A" }} />;

			case "docx":
				return <FileWordFilled style={{ fontSize: 60, marginBottom: 20, color: "#1B5EBE" }} />;

			case "doc":
				return <FileWordFilled style={{ fontSize: 60, marginBottom: 20, color: "#1B5EBE" }} />;

			default:
				return <FileTextFilled style={{ fontSize: 60, marginBottom: 20 }} />;
		}
	};

	const renderPreviewFileSize = () => {
		if (!previewFileSize) {
			return;
		}
		if (previewFileSize === previewOriginalFileSize) {
			return <span>~{previewFileSize}</span>;
		} else {
			return <span>{previewOriginalFileSize + " >>> " + previewFileSize}</span>;
		}
	};

	const content = (
		<Dragger
			accept={allowedExtension?.map((item) => "." + item).join(",")}
			maxCount={1}
			showUploadList={false}
			beforeUpload={_handleSelectImage}
			customRequest={({ onSuccess }) => onSuccess && onSuccess("OK")}
			disabled={disabled}
		>
			<div className="ant-upload-preview-container">
				{previewImage ? (
					<>
						<img
							className={`ant-upload-preview ${props.isCircle ? 'circle-preview' : ''}`}
							alt="preview"
							src={previewImage}
						/>
						{isResizingLoading ? (
							<span style={{ color: "orange" }}>Resizing... Please wait...</span>
						) : (
							false
						)}
					</>
				) : previewName ? (
					<React.Fragment>
						<PreviewIcon />
						<span className="ant-upload-file-name">{previewName}</span>
					</React.Fragment>
				) : isResizingLoading ? (
					<div className="ant-upload-drag-icon">
						<Spin size="large" />
						<p style={{ color: "orange" }}>
							Resizing...
							<br />
							Please wait...
						</p>
					</div>
				) : (
					<div className="ant-upload-drag-icon">
						<FileUpload />
						<p>
							Drag and drop it here
							<br />
							or
							<br />
							Upload here
						</p>
					</div>
				)}
			</div>
			{showInfo && (
				<p className="ant-upload-hint">
					Maksimum file {maxFileSize}Mb {props.enableAutoResizeForImage && " (auto compress)"}
					. Extensi {allowedExtension?.map((item) => item.toUpperCase()).join(", ")}
					{additionalHint && (
						<>
							{additionalHint}
						</>
					)}
					{renderPreviewFileSize()}
				</p>
			)}
		</Dragger>
	)

	return props.withImgCrop ? (
		<ImgCrop
			aspect={props.imgCropAspect}
			fillColor="transparent"
		>
			{content}
		</ImgCrop>
	) : (
		content
	);
};
export default UploadInput;
